import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'underscore';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { ADVANCED_ANALYTICS, IBestDataset, IPlotVariable } from '../../../../types/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart';
import { MarkerItem } from '../../../../types/home';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { formatRangeEnd, formatRangePartToTime } from '../../../../utils/dateRange';

interface UseTidalLevelParams {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  onDatasetChange: (item: string) => void;
  dateRange: [moment.Moment, moment.Moment];
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  tidalLevelValueImage: string;
  tidalValueLinks: string[];
  error: string;
  updating: boolean;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
}

const supportedItems = ['WL', 'WL_Tide'];

export const useTidalParameters = (params: UseTidalLevelParams) => {
  const {
    coordinates,
    previewDatasetId,
    datasets,
    onDatasetChange,
    dateRange,
    enrichedMetadata,
    onUpdate,
    tidalLevelValueImage,
    pointName,
    userInput,
    updateUserInput,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  } = params;

  const selfTriggered = useRef(false);
  const defaultsAppliedForRef = useRef<string | null>(null);

  const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([]);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(userInput.TidalLevel.variable);
  const [startDate, setStartDate] = useState(
    userInput.TidalLevel.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(userInput.TidalLevel.endDate || moment(dateRange[1]));
  const [supported, setSupported] = useState<boolean>(false);

  const getImage = useCallback(
    (indexX: IPlotVariable, orderId: string, startDate: string, endDate: string) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.TIDAL_LEVEL,
          DatasetId: previewDatasetId,
          OriginalDatasetId: previewDatasetId,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  );

  const onUpdateTidalLevel = useCallback(
    (dateRange?: any) => {
      if (enrichedMetadata && variableX) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId && task.CoordinatesString === coordinates.join()
        );

        if (task) {
          getImage(
            variableX,
            task.OrderId,
            formatRangePartToTime(dateRange ? dateRange[0] : startDate),
            formatRangeEnd(dateRange ? dateRange[1] : endDate)
          );
        }
      }
    },
    [enrichedMetadata, previewDatasetId, startDate, endDate, variableX, coordinates, getImage]
  );

  const doVariableChange = useCallback(
    (active: number) => {
      const item = listVariablesX.find((e) => e.Index === active);
      setVariableX(item!);
    },
    [listVariablesX]
  );

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const onSelectDataset = useCallback(
    (value: string) => {
      onDatasetChange(value);
    },
    [onDatasetChange]
  );

  const handleCheckboxChange = useCallback(() => {
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
    selfTriggered.current = true;
  }, [startDate, endDate, setGlobalDateRange, isForAllAnalytics, setIsForAllAnalytics]);

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      return isAfterEndDate ? isAfterEndDate : current.isBefore(dateRange[0], 'day');
    },
    [endDate, dateRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      return isBeforeStartDate ? isBeforeStartDate : current.isAfter(dateRange[1], 'day');
    },
    [startDate, dateRange]
  );

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [datasets]);

  useEffect(() => {
    updateUserInput({
      TidalLevel: {
        dataset: previewDatasetId,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [previewDatasetId, variableX, startDate, endDate, updateUserInput]);

  useEffect(() => {
    if (isForAllAnalytics && tidalLevelValueImage !== '') {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateTidalLevel(globalDateRange);
        setStartDate(globalDateRange[0]);
        setEndDate(globalDateRange[1]);
      }
    }
  }, [isForAllAnalytics, tidalLevelValueImage, globalDateRange]);

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId && task.CoordinatesString === coordinates.join()
      );
      if (task && task.ItemsInfo) {
        const itemsInfos = task.ItemsInfo.filter(
          (itemInfo: IPlotVariable) =>
            itemInfo.ShortName && supportedItems.includes(itemInfo.ShortName)
        );
        setListVariableX(itemsInfos);
        if (itemsInfos.length > 0) {
          if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates)) {
            let initialDateRange = dateRange;
            setEndDate(moment(dateRange[1]));
            if (isForAllAnalytics) {
              initialDateRange = globalDateRange as [moment.Moment, moment.Moment];
              setStartDate(initialDateRange[0]);
              setEndDate(initialDateRange[1]);
            } else {
              setStartDate(moment(initialDateRange[0]));
              setEndDate(moment(initialDateRange[1]));
            }
            setSupported(true);
            selfTriggered.current = true;
            const selectionKey = `${previewDatasetId}|${coordinates.join(',')}|${
              itemsInfos.length
            }`;
            if (defaultsAppliedForRef.current !== selectionKey) {
              defaultsAppliedForRef.current = selectionKey;
              const restoredVariable = userInput.TidalLevel.variable
                ? itemsInfos.find((item) => item.Index === userInput.TidalLevel.variable?.Index)
                : undefined;
              const indexX = restoredVariable ?? itemsInfos[0];
              setVariableX(indexX);
              let effectiveDateRange = initialDateRange;
              if (
                !isForAllAnalytics &&
                userInput.TidalLevel.startDate &&
                userInput.TidalLevel.endDate
              ) {
                effectiveDateRange = [userInput.TidalLevel.startDate, userInput.TidalLevel.endDate];
                setStartDate(effectiveDateRange[0]);
                setEndDate(effectiveDateRange[1]);
              }
              getImage(
                indexX,
                task.OrderId,
                formatRangePartToTime(effectiveDateRange[0]),
                formatRangeEnd(effectiveDateRange[1])
              );
            }
          }
        } else {
          setSupported(false);
        }
      }
    }
  }, [
    previewDatasetId,
    datasets,
    enrichedMetadata,
    coordinates,
    globalDateRange,
    isForAllAnalytics,
  ]);

  return {
    listVariablesX,
    previewDatasetId,
    variableX,
    startDate,
    endDate,
    supported,
    categories,
    onUpdateTidalLevel,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  };
};
